import { useLayoutEffect, useRef, useState } from 'react';
import { ThemeContext } from '../../constants/themes';
import { useHistory, useLocation } from "react-router-dom";
import Type from '../../components/atoms/Type';
import Button from '../../components/atoms/Button';
import Loader from '../../components/atoms/Loader';
import Navbar from '../../components/molecules/Navbar';
import { withAuth0 } from '@auth0/auth0-react';
import ReactAudioPlayer from 'react-audio-player';
import {
  EmailShareButton,
  FacebookShareButton,
  LinkedinShareButton,
  TwitterShareButton,
} from "react-share";
import { Helmet } from "react-helmet";
import { ReactComponent as Email } from '../../assets/img/email.svg';
import { ReactComponent as Facebook } from '../../assets/img/facebook.svg';
import { ReactComponent as Twitter } from '../../assets/img/twitter.svg';
import { ReactComponent as LinkedIn } from '../../assets/img/linkedin.svg';
import { ReactComponent as Checkmark } from '../../assets/img/checkmark.svg';
import { ReactComponent as Link } from '../../assets/img/link.svg';
import Parrot from '../../api/parrot';
import './index.css';

const useQuery = () => {
  return new URLSearchParams(useLocation().search);
}

const parrot = new Parrot();

const Listen = (props) => {
  let history = useHistory();
  const inputRef = useRef(null);
  const audioRef = useRef(null);

  const query = useQuery();

  const [state, setState] = useState({
    isLoading: true,
    justCopiedLink: false,
    userHasRequested: false,
    credits: undefined,
    pendingRecordingRequestIsLoading: false,
    pendingRecordingRequest: undefined,
    content: undefined
  });

  // Get content ID if it's directly provided
  let contentId = query.get("content_id");
  let contentIdIsDefined = contentId !== undefined && contentId !== 'undefined' && contentId !== null && contentId !== 'null';
  // Get article URL if it's directly provided
  let articleUrl = query.get("article_url");
  let articleUrlIsDefined = articleUrl !== undefined && articleUrl !== 'undefined' && articleUrl !== null && articleUrl !== 'null';
  // Get audio URL if it's directly provided
  let audioUrl = query.get("audio_url");
  let audioUrlIsDefined = audioUrl !== undefined && audioUrl !== 'undefined' && audioUrl !== null && audioUrl !== 'null';

  let shareableLink = `${window.location.origin}/listen/?`;
  if (contentIdIsDefined) {
    shareableLink = `${shareableLink}content_id=${contentId}`;
  }
  if (articleUrlIsDefined) {
    shareableLink = `${shareableLink}article_url=${articleUrl}`;
  }

  const fetchContentData = async () => {
    // If audio URL is defined, just pull everything from query params
    if (audioUrlIsDefined) {
      setState({
        ...state,
        isLoading: false,
        userHasRequested: true,
        content: {
          title: query.get("title"),
          url: query.get("url"),
          audio_url: audioUrl
        }
      });
    } else if (contentIdIsDefined) {
      parrot.getContent({ content_id: contentId }).then(async contentResponse => {
        document.title = `ReadMeThis: "${contentResponse?.title}"`;
        if (!props.auth0.isAuthenticated) {
          setState({
            ...state,
            isLoading: false,
            userHasRequested: false,
            content: contentResponse
          });
          // Only 60 second preview allowed
          audioRef?.current?.audioEl.current.addEventListener('timeupdate', () => {
            if (audioRef.current.audioEl.current.currentTime > 60) {
              audioRef.current.audioEl.current.pause();
              audioRef.current.audioEl.current.currentTime = 0;
            }
          });
        } else {
          const accessToken = await props.auth0.getAccessTokenSilently();
          parrot.userHasRequested(accessToken, contentId).then(hasRequestedResponse => {
            if (hasRequestedResponse?.content_requested) {
              setState({
                ...state,
                isLoading: false,
                userHasRequested: true,
                content: contentResponse
              });
            } else {
              try {
                parrot.getArticleInfo(accessToken, { content_id: contentId }).then(articleInfoResponse => {
                  setState({
                    ...state,
                    isLoading: false,
                    userHasRequested: false,
                    pendingRecordingRequest: articleInfoResponse,
                    content: contentResponse
                  });
                  // Only 60 second preview allowed
                  audioRef?.current?.audioEl.current.addEventListener('timeupdate', () => {
                    if (audioRef.current.audioEl.current.currentTime > 60) {
                      audioRef.current.audioEl.current.pause();
                      audioRef.current.audioEl.current.currentTime = 0;
                    }
                  });
                });
              } catch (err) {
                console.log(err);
                // Only 60 second preview allowed
                audioRef?.current?.audioEl.current.addEventListener('timeupdate', () => {
                  if (audioRef.current.audioEl.current.currentTime > 60) {
                    audioRef.current.audioEl.current.pause();
                    audioRef.current.audioEl.current.currentTime = 0;
                  }
                });
              }
            }
          });
        }
      });
    } else if (articleUrlIsDefined) {
      parrot.getContent({ article_url: articleUrl }).then(async contentResponse => {
        document.title = `ReadMeThis: "${contentResponse?.title}"`;
        if (!props.auth0.isAuthenticated) {
          setState({
            ...state,
            isLoading: false,
            userHasRequested: false,
            content: contentResponse
          });
        } else {
          const accessToken = await props.auth0.getAccessTokenSilently();
          parrot.userHasRequested(accessToken, contentId).then(hasRequestedResponse => {
            if (hasRequestedResponse?.content_requested) {
              setState({
                ...state,
                isLoading: false,
                userHasRequested: true,
                content: contentResponse
              });
            } else {
              try {
                parrot.getArticleInfo(accessToken, { article_url: articleUrl }).then(articleInfoResponse => {
                  setState({
                    ...state,
                    isLoading: false,
                    userHasRequested: false,
                    pendingRecordingRequest: articleInfoResponse,
                    content: contentResponse
                  });
                });
              } catch (err) {
                console.log(err);
              }
            }
          });
        }
      });
    } else {
      setState({
        ...state,
        isLoading: false
      });
    }
  }

  useLayoutEffect(() => {
    // Nuke local storage if possible and add to URL instead
    if (localStorage.getItem('contentId')) {
      const contentId = localStorage.getItem('contentId');
      localStorage.removeItem('contentId');
      window.location.href = `${window.location.origin}/listen/?content_id=${contentId}`;
    } else if (localStorage.getItem('articleUrl')) {
      const articleUrl = localStorage.getItem('articleUrl');
      localStorage.removeItem('articleUrl');
      window.location.href = `${window.location.origin}/listen/?article_url=${articleUrl}`;
    } else {
      props.mixpanel?.track("Listen Page Visit");
      fetchContentData();
    }
  }, []);

  const copyToClipboard = (e) => {
    inputRef.current.select();
    document.execCommand('copy');
    e.target.focus();
    setState({
      ...state,
      justCopiedLink: true
    });
  }

  const signIn = () => {
    if (contentIdIsDefined) {
      localStorage.setItem('contentId', contentId);
    } else {
      localStorage.setItem('articleUrl', articleUrl);
    }
    props.auth0.loginWithRedirect({
      screen_hint: 'signin',
      redirectUri: `${window.location.origin}/listen`
    });
  }

  const signUp = () => {
    props.auth0.loginWithRedirect({
      screen_hint: 'signup',
      redirectUri: window.location.origin
    });
  }

  const signUpToLanding = () => {
    props.auth0.loginWithRedirect({
      screen_hint: 'signup',
      redirectUri: `${window.location.origin}/dashboard`
    });
  }

  const submitRecordingRequest = async () => {
    props.mixpanel?.track("Listen Page Article Request");
    setState({
      ...state,
      pendingRecordingRequestIsLoading: true,
      pendingRecordingRequest: undefined
    });
    const accessToken = await props.auth0.getAccessTokenSilently();
    if (contentIdIsDefined) {
      parrot.confirmRequestNarration(accessToken, { content_id: contentId }).then(res => {
        if (state.content.audio_url) {
          window.location.reload();
        } else {
          history.push('/dashboard');
        }
      });
    } else {
      parrot.confirmRequestNarration(accessToken, { article_url: articleUrl }).then(res => {
        if (state.content.audio_url) {
          window.location.reload();
        } else {
          history.push('/dashboard');
        }
      });
    }
  }

  console.log(state);

  if (!state.isLoading && state.content === undefined) {
    history.push("/");
    return null;
  } else {
    return (
      <ThemeContext.Consumer>
        {themeContext => (
          <>
            <Helmet>
              <meta name="viewport" content="width=device-width, initial-scale=1, shrink-to-fit=no" />
              <link rel="manifest" href="%PUBLIC_URL%/manifest.json" />
              <meta name="twitter:card" content="summary_large_image" />
              <meta name="theme-color" content="#000000" />
              {/* <!-- Title --> */}
              <title>ReadMeThis: "{state.content?.title || ''}"</title>
              <meta property="og:title" content={`ReadMeThis: "${state.content?.title}"`} />
              <meta name="twitter:title" content={`ReadMeThis: "${state.content?.title}"`} />
              {/* <!-- Preview Image --> */}
              <meta property="og:image" content="SAMPLE_PREVIEW_IMAGE" />
              <meta name="twitter:image" content="SAMPLE_PREVIEW_IMAGE" />
              {/* <!-- Description --> */}
              <meta name="description" content={`Article: ${state.content?.url}`} />
              <meta name="twitter:description" content={`Article: ${state.content?.url}`} />
            </Helmet>
            {state.isLoading ?
              <div className="background" style={{ backgroundColor: themeContext.theme.body }}>
                <Navbar promptDashboard promptSettings={props.auth0.isAuthenticated} authed={props.auth0.isAuthenticated} />
                <div className="dashboard-content-div">
                  <Loader />
                </div>
              </div>
              :
              <div className="background" style={{ backgroundColor: themeContext.theme.body }}>
                <Navbar promptDashboard promptSettings={props.auth0.isAuthenticated} authed={props.auth0.isAuthenticated} />
                <div className="dashboard-content-div">
                  {contentIdIsDefined || articleUrlIsDefined ?
                    <div className="share-sheet-nav">
                      <Type style={{ margin: '0px 8px' }}>Share:</Type>
                      <EmailShareButton style={{ margin: '0px 6px' }} subject="Check out this narrated article" body="Narrated through ReadMeThis:" url={shareableLink}>
                        <Email width={19} fill={themeContext.theme.text} style={{ marginBottom: -3.5, padding: 0 }} />
                      </EmailShareButton>
                      <FacebookShareButton style={{ margin: '0px 6px' }} url={shareableLink}>
                        <Facebook width={19} fill={themeContext.theme.text} style={{ marginBottom: -3.5, padding: 0 }} />
                      </FacebookShareButton>
                      <TwitterShareButton style={{ margin: '0px 6px' }} url={shareableLink}>
                        <Twitter width={19} fill={themeContext.theme.text} style={{ marginBottom: -3.5, padding: 0 }} />
                      </TwitterShareButton>
                      <LinkedinShareButton style={{ margin: '0px 6px' }} url={shareableLink}>
                        <LinkedIn width={19} fill={themeContext.theme.text} style={{ marginBottom: -3.5, padding: 0 }} />
                      </LinkedinShareButton>
                      <Type style={{ margin: '0px 8px' }} >or</Type>
                      <input style={{ opacity: '0', position: 'absolute', left: '-1000px', top: '-1000px' }} ref={inputRef} value={shareableLink}></input>
                      <a onClick={copyToClipboard}>
                        {state.justCopiedLink ?
                          <div className="copy-link-row">
                            <Type style={{ marginRight: 6, fontWeight: '900', textDecoration: 'underline' }}>Copied</Type>
                            <Checkmark width={19} fill={themeContext.theme.text} style={{ marginBottom: 0, padding: 0 }} />
                          </div>
                          :
                          <div className="copy-link-row">
                            <Type style={{ marginRight: 6, fontWeight: '900', textDecoration: 'underline' }}>Copy Link</Type>
                            <Link width={19} fill={themeContext.theme.text} style={{ marginBottom: 0, padding: 0 }} />
                          </div>
                        }
                      </a>
                    </div>
                    :
                    null
                  }
                  <Type type="h1" style={{ marginBottom: 0 }}>{state.content?.title || ''}</Type>
                  <Type><a href={`${articleUrlIsDefined ? '' : 'https://'}${state.content?.url}`} target="_blank" style={{ color: themeContext.theme.text }}>{state.content?.url || ''}</a></Type>
                  {state.content?.audio_url ?
                    <>
                      {(!props.auth0.isAuthenticated && !audioUrlIsDefined) || !state.userHasRequested ?
                        <Type type="caption" style={{ color: themeContext.theme.text }}>Enjoy this 60-second narration preview.</Type>
                        :
                        <br />
                      }
                      <ReactAudioPlayer
                        ref={audioRef}
                        src={state.content?.audio_url || undefined}
                        autoPlay
                        controls
                        controlsList="nodownload"
                        id="audio-player"
                        className="listen-audio-player"
                        style={{ borderColor: themeContext.theme.text, color: themeContext.theme.text }}
                      />
                    </>
                    :
                    <>
                      <br />
                      <ReactAudioPlayer
                        ref={audioRef}
                        src={undefined}
                        autoPlay
                        controls
                        controlsList="nodownload"
                        id="audio-player"
                        className="listen-audio-player"
                        style={{ opacity: '0.2', borderColor: themeContext.theme.text, color: themeContext.theme.text }}
                      />
                    </>
                  }
                  {state.userHasRequested ?
                    // If it's an audio URL, let them see the ad regardless
                    (audioUrlIsDefined ?
                      <>
                        <hr />
                        <Type style={{ margin: 0 }}>Want a different piece of content narrated?</Type>
                        <Type type="h2" style={{ maxWidth: '20em' }}>Have newsletters, articles, or blog posts read to you by real humans within 24 hours.</Type>
                        <Type style={{ margin: 0 }}>Sent directly to wherever you listen to podcasts.</Type>
                        <br />
                        <Button value="Try it for free" onClick={signUpToLanding} />
                        <Type type="caption">No credit card required.</Type>
                      </>
                      :
                      null
                    )
                    :
                    (!props.auth0.isAuthenticated ?
                      <div style={{ width: '100%' }}>
                        {state.content.audio_url ?
                          <Type type="h2" style={{ margin: '1em 0 0.0 0' }}>Listen to the full narration of this article:</Type>
                          :
                          <Type type="h2" style={{ margin: '1em 0 0.0 0' }}>Sign in to listen to this human-narrated article:</Type>
                        }
                        <br />
                        <Button value="Sign in" onClick={signIn} />
                        <hr />
                        <Type style={{ margin: 0 }}>Want a different piece of content narrated?</Type>
                        <Type type="h2" style={{ maxWidth: '20em' }}>Have newsletters, articles, or blog posts read to you by real humans within 24 hours.</Type>
                        <Type style={{ margin: 0 }}>Sent directly to wherever you listen to podcasts.</Type>
                        <br />
                        <Button value="Try it for free" onClick={signUp} />
                        <Type type="caption">No credit card required.</Type>
                      </div>
                      :
                      <div style={{ width: '100%' }}>
                        {!state.pendingRecordingRequestIsLoading ?
                          state.pendingRecordingRequest?.user_credits === null ?
                            <div>
                              {/* TODO: they can request this article for free */}
                              <Type type="h2" style={{ margin: '1em 0 0.0 0' }}>Confirm your request for the full narration of this article:</Type>
                              <Type>This, your first request, is 100% free. No credit card required.</Type>
                              <Button disabled={state.pendingRecordingRequest === undefined} value="Submit request" onClick={submitRecordingRequest} />
                            </div>
                            :
                            <div>
                              {/* TODO: they can pay to request this article */}
                              {state.pendingRecordingRequest?.credit_cost > state.pendingRecordingRequest?.user_credits ?
                                <>
                                  <Type type="h2" style={{ margin: '1em 0 0.0 0' }}>Sorry, you need more credits to request the full narration of this article.</Type>
                                  <div>
                                    <Type>You have: {state.pendingRecordingRequest?.user_credits} credits</Type>
                                    <Type>Cost: {state.pendingRecordingRequest?.credit_cost} credits</Type>
                                  </div>
                                  <Button value="Get more credits" onClick={() => history.push('/dashboard')} />
                                </>
                                :
                                <>
                                  <Type type="h2" style={{ margin: '1em 0 0.0 0' }}>Confirm your request for the full narration of this article:</Type>
                                  <div>
                                    <Type>You have: {state.pendingRecordingRequest?.user_credits} credits</Type>
                                    <Type>Cost: {state.pendingRecordingRequest?.credit_cost} credits</Type>
                                  </div>
                                  <Button disabled={state.pendingRecordingRequest === undefined} value="Submit" onClick={submitRecordingRequest} />
                                </>
                              }
                            </div>
                          :
                          <div style={{ paddingTop: 40 }}>
                            <Loader />
                          </div>
                        }
                      </div>
                    )
                  }
                </div>
              </div>
            }
          </>
        )}
      </ThemeContext.Consumer>
    );
  }
}

export default withAuth0(Listen);
